import {
  makeApiProvider,
  useApiInfiniteQuery,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type { IReceiptBatchApi } from './api';
import { ReceiptBatchApi } from './api';
import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import type {
  ReceiptBatchControllerBodyTypes,
  ReceiptBatchControllerQueryTypes,
  ReceiptBatchControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';

const ApiContext = createContext<IReceiptBatchApi | undefined>(undefined);
const { useApi: useReceiptBatchApi, ApiProvider: ReceiptBatchApiProvider } =
  makeApiProvider({
    name: 'ReceiptBatch',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new ReceiptBatchApi(tenantId, auth, apiUrl),
  });

export {
  useReceiptBatchApi,
  ReceiptBatchApiProvider,
  ApiContext as ReceiptBatchApiContext,
};
export type { IReceiptBatchApi } from './api';

export {
  isSubmitReceiptBatchServiceProviderUserIncompleteException,
  isSubmitReceiptBatchResidentInfoIncompleteException,
  isSubmitReceiptBatchNursingHomeInfoIncompleteException,
  isSubmitReceiptBatchServiceProviderInfoIncompleteException,
  isSubmitReceiptBatchDateError,
} from './api';

export const GET_RECEIPT_BATCH_LIST_KEY = 'getReceiptBatchList';
export const GET_RECEIPT_BATCH_LIST = (
  params: ReceiptBatchControllerQueryTypes['getReceiptBatchList']
): QueryKey => [
  GET_RECEIPT_BATCH_LIST_KEY,
  {
    ...params,
  },
];
export const GET_RECEIPT_BATCH_LIST_INFINITE = (
  params: Omit<ReceiptBatchControllerQueryTypes['getReceiptBatchList'], 'page'>
): QueryKey => [
  GET_RECEIPT_BATCH_LIST_KEY,
  {
    ...params,
    infinite: true,
  },
];
export const GET_RECEIPT_BATCH = (receiptBatchId?: string): QueryKey => [
  'getReceiptBatch',
  receiptBatchId,
];

export const POST_RECEIPT_BATCH_KEY = ['postReceiptBatch'];
export const PUT_RECEIPT_BATCH_KEY = ['putReceiptBatch'];
export const PATCH_RECEIPT_BATCH_KEY = ['patchReceiptBatch'];
export const POST_RECEIPT_BATCH_ENTRY_KEY = ['postReceiptBatchEntry'];
export const PATCH_RECEIPT_BATCH_ENTRY_KEY = ['patchReceiptBatchEntry'];
export const DELETE_RECEIPT_BATCH_KEY = ['deleteReceiptBatch'];
export const DELETE_RECEIPT_BATCH_ENTRY_KEY = ['deleteReceiptBatchEntry'];
export const SUBMIT_RECEIPT_BATCH_KEY = ['submitReceiptBatch'];
export const RETRACT_RECEIPT_BATCH_KEY = ['retractReceiptBatch'];
export const GENERATE_RECEIPT_BATCH_PREVIEW_PDF_KEY = [
  'generateReceiptBatchPreviewPdf',
];

export const useGetReceiptBatchList = (
  params: ReceiptBatchControllerQueryTypes['getReceiptBatchList'],
  options: UseQueryOptions<
    ReceiptBatchControllerResultTypes['getReceiptBatchList']
  >
): UseQueryResult<ReceiptBatchControllerResultTypes['getReceiptBatchList']> => {
  const select = useCallback(
    (
      data: ReceiptBatchControllerResultTypes['getReceiptBatchList']
    ): ReceiptBatchControllerResultTypes['getReceiptBatchList'] => {
      return {
        ...data,
        data: data.data.map((row) => ({
          ...row,
          receiptDate: row.receiptDate ? new Date(row.receiptDate) : undefined,
          minReceiptDate: row.minReceiptDate
            ? new Date(row.minReceiptDate)
            : undefined,
          maxReceiptDate: row.maxReceiptDate
            ? new Date(row.maxReceiptDate)
            : undefined,
          createdOn: new Date(row.createdOn),
          updatedOn: new Date(row.updatedOn),
          acceptedDate: row.acceptedDate
            ? new Date(row.acceptedDate)
            : undefined,
        })),
      };
    },
    []
  );
  return useApiQuery(
    useReceiptBatchApi,
    GET_RECEIPT_BATCH_LIST(params),
    (api) => {
      return api.getReceiptBatchList({
        params,
      });
    },
    {
      ...options,
      select,
    }
  );
};

export const useGetReceiptBatchListInfiniteQuery = (
  params: Omit<ReceiptBatchControllerQueryTypes['getReceiptBatchList'], 'page'>,
  options: UseInfiniteQueryOptions<
    ReceiptBatchControllerResultTypes['getReceiptBatchList']
  >
): UseInfiniteQueryResult<
  ReceiptBatchControllerResultTypes['getReceiptBatchList']
> => {
  const select = useCallback(
    (
      data: InfiniteData<
        ReceiptBatchControllerResultTypes['getReceiptBatchList']
      >
    ): InfiniteData<
      ReceiptBatchControllerResultTypes['getReceiptBatchList']
    > => {
      return {
        ...data,
        pages: [
          ...data.pages.map((page) => ({
            ...page,
            data: page.data.map((item) => ({
              ...item,
              receiptDate: item.receiptDate
                ? new Date(item.receiptDate)
                : undefined,
              minReceiptDate: item.minReceiptDate
                ? new Date(item.minReceiptDate)
                : undefined,
              maxReceiptDate: item.maxReceiptDate
                ? new Date(item.maxReceiptDate)
                : undefined,
              createdOn: new Date(item.createdOn),
              updatedOn: new Date(item.updatedOn),
              acceptedDate: item.acceptedDate
                ? new Date(item.acceptedDate)
                : undefined,
            })),
          })),
        ],
      };
    },
    []
  );

  return useApiInfiniteQuery<IReceiptBatchApi, any, any>(
    useReceiptBatchApi,
    GET_RECEIPT_BATCH_LIST_INFINITE(params),
    (api, page) => {
      return api.getReceiptBatchList({
        params: {
          ...params,
          page,
        },
      });
    },
    {
      ...options,
      select,
    }
  );
};

function selectGetReceiptBatch(
  data: ReceiptBatchControllerResultTypes['getReceiptBatch']
): ReceiptBatchControllerResultTypes['getReceiptBatch'] {
  return {
    ...data,
    receiptDate: data.receiptDate ? new Date(data.receiptDate) : undefined,
    createdOn: new Date(data.createdOn),
    updatedOn: new Date(data.updatedOn),
    acceptedDate: data.acceptedDate ? new Date(data.acceptedDate) : undefined,
  };
}

export const useGetReceiptBatchCallback = (
  receiptBatchId: string
): (() => Promise<ReceiptBatchControllerResultTypes['getReceiptBatch']>) => {
  const api = useReceiptBatchApi();
  return useCallback(async () => {
    const result = await api.getReceiptBatch({
      params: { batchId: receiptBatchId },
    });
    return selectGetReceiptBatch(result);
  }, [api, receiptBatchId]);
};

export const useGetReceiptBatch = (
  receiptBatchId: string | undefined,
  options?: UseQueryOptions<
    ReceiptBatchControllerResultTypes['getReceiptBatch']
  >
): UseQueryResult<ReceiptBatchControllerResultTypes['getReceiptBatch']> => {
  return useApiQuery(
    useReceiptBatchApi,
    GET_RECEIPT_BATCH(receiptBatchId),
    (api) => {
      if (receiptBatchId === undefined) {
        throw new Error('receiptBatchId is undefined');
      }

      return api.getReceiptBatch({
        params: {
          batchId: receiptBatchId,
        },
      });
    },
    {
      select: selectGetReceiptBatch,
      enabled: receiptBatchId !== undefined,
      ...options,
    }
  );
};

export const usePostReceiptBatch = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['postReceiptBatch'],
  unknown,
  ReceiptBatchControllerBodyTypes['postReceiptBatch']
> => {
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['postReceiptBatch'],
    unknown,
    ReceiptBatchControllerBodyTypes['postReceiptBatch']
  >(POST_RECEIPT_BATCH_KEY, (data) =>
    api
      .postReceiptBatch({
        body: data,
      })
      .then((res) => ({
        ...res,
        receiptDate: res.receiptDate ? new Date(res.receiptDate) : undefined,
        createdOn: new Date(res.createdOn),
        updatedOn: new Date(res.updatedOn),
        acceptedDate: res.acceptedDate ? new Date(res.acceptedDate) : undefined,
      }))
  );
  return result;
};

export const usePutReceiptBatch = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['putReceiptBatch'],
  unknown,
  ReceiptBatchControllerBodyTypes['putReceiptBatch'] & {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['putReceiptBatch'],
    unknown,
    ReceiptBatchControllerBodyTypes['putReceiptBatch'] & {
      batchId: string;
    }
  >(
    PUT_RECEIPT_BATCH_KEY,
    (data) =>
      api
        .putReceiptBatch({
          params: { batchId: data.batchId },
          body: data,
        })
        .then((res) => ({
          ...res,
          receiptDate: res.receiptDate ? new Date(res.receiptDate) : undefined,
          createdOn: new Date(res.createdOn),
          updatedOn: new Date(res.updatedOn),
          acceptedDate: res.acceptedDate
            ? new Date(res.acceptedDate)
            : undefined,
        })),
    {
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]),
        ]);
      },
    }
  );
  return result;
};

export const usePatchReceiptBatch = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['patchReceiptBatch'],
  unknown,
  ReceiptBatchControllerBodyTypes['patchReceiptBatch'] & {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['patchReceiptBatch'],
    unknown,
    ReceiptBatchControllerBodyTypes['patchReceiptBatch'] & {
      batchId: string;
    }
  >(
    PATCH_RECEIPT_BATCH_KEY,
    (data) =>
      api
        .patchReceiptBatch({
          params: { batchId: data.batchId },
          body: data,
        })
        .then((res) => ({
          ...res,
          receiptDate: res.receiptDate ? new Date(res.receiptDate) : undefined,
          createdOn: new Date(res.createdOn),
          updatedOn: new Date(res.updatedOn),
          acceptedDate: res.acceptedDate
            ? new Date(res.acceptedDate)
            : undefined,
        })),
    {
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]),
        ]);
      },
    }
  );
  return result;
};

export const usePostReceiptBatchEntry = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['postReceiptBatchEntry'],
  unknown,
  ReceiptBatchControllerBodyTypes['postReceiptBatchEntry'] & {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['postReceiptBatchEntry'],
    unknown,
    ReceiptBatchControllerBodyTypes['postReceiptBatchEntry'] & {
      batchId: string;
    }
  >(
    POST_RECEIPT_BATCH_ENTRY_KEY,
    (data) =>
      api.postReceiptBatchEntry({
        params: { batchId: data.batchId },
        body: data,
      }),
    {
      onSuccess: async (data, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]),
        ]);
      },
    }
  );
  return result;
};

export const usePatchReceiptBatchEntry = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['patchReceiptBatchEntry'],
  unknown,
  ReceiptBatchControllerBodyTypes['patchReceiptBatchEntry'] & {
    batchId: string;
    entryId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['patchReceiptBatchEntry'],
    unknown,
    ReceiptBatchControllerBodyTypes['patchReceiptBatchEntry'] & {
      batchId: string;
      entryId: string;
    }
  >(
    PATCH_RECEIPT_BATCH_ENTRY_KEY,
    (data) =>
      api.patchReceiptBatchEntry({
        params: {
          batchId: data.batchId,
          entryId: data.entryId,
        },
        body: data,
      }),
    {
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]),
        ]);
      },
    }
  );
  return result;
};

export const useDeleteReceiptBatch = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['deleteReceiptBatch'],
  unknown,
  {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['deleteReceiptBatch'],
    unknown,
    {
      batchId: string;
    }
  >(
    DELETE_RECEIPT_BATCH_KEY,
    (data) =>
      api.deleteReceiptBatch({
        params: { batchId: data.batchId },
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]);
      },
    }
  );
  return result;
};

export const useDeleteReceiptBatchEntry = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['deleteReceiptBatchEntry'],
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['deleteReceiptBatchEntry'],
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >(
    DELETE_RECEIPT_BATCH_ENTRY_KEY,
    (data) =>
      api.deleteReceiptBatchEntry({
        params: { batchId: data.batchId, entryId: data.entryId },
      }),
    {
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries(GET_RECEIPT_BATCH_LIST_KEY),
        ]);
      },
    }
  );
  return result;
};

export const useSubmitReceiptBatch = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['submitReceiptBatch'],
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['submitReceiptBatch'],
    unknown,
    {
      batchId: string;
    }
  >(
    SUBMIT_RECEIPT_BATCH_KEY,
    (data) =>
      api.submitReceiptBatch({
        params: { batchId: data.batchId },
      }),
    {
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]),
        ]);
      },
    }
  );
  return result;
};

export const useRetractReceiptBatch = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['retractReceiptBatch'],
  unknown,
  {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['retractReceiptBatch'],
    unknown,
    {
      batchId: string;
    }
  >(
    RETRACT_RECEIPT_BATCH_KEY,
    (data) =>
      api.retractReceiptBatch({
        params: { batchId: data.batchId },
      }),
    {
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries(GET_RECEIPT_BATCH(variables.batchId)),
          queryClient.invalidateQueries([GET_RECEIPT_BATCH_LIST_KEY]),
        ]);
      },
    }
  );
  return result;
};

export const useGenerateReceiptPdf = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['generateReceiptPdf'],
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['generateReceiptPdf'],
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >(
    RETRACT_RECEIPT_BATCH_KEY,
    (data) =>
      api.generateReceiptBatchEntryPdf({
        params: { batchId: data.batchId, entryId: data.entryId },
      }),
    {}
  );
  return result;
};

export const useGenerateReceiptBatchPdf = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['generateReceiptBatchPdf'],
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['generateReceiptBatchPdf'],
    unknown,
    {
      batchId: string;
    }
  >(
    GENERATE_RECEIPT_BATCH_PREVIEW_PDF_KEY,
    (data) =>
      api.generateReceiptBatchPdf({
        params: { batchId: data.batchId },
      }),
    {}
  );
  return result;
};
